import styled, { css } from 'styled-components';
import _ from 'lodash';
import Button from 'components/Button/Button';
import {
  colorVariables,
  otherVariables,
  fontVariables,
  fontFamilyVariables,
} from 'theme/variables';

export const SliderStyled = styled.div`
  width: 100%;
  position: relative;

  * {
    user-select: none;
  }
`;

export const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  height: ${otherVariables.trackHeight};
  left: 0;
  top: calc(50% - 2px);
  outline: none;
  background-color: rgba(255, 255, 255, .2);
  transition: transform .1s;

  ${({ isMobile }) =>
    !isMobile &&
    css`
      ${TrackContent}:hover & {
        transform: scaleY(1.5);
      }
    `}
  
  ${({ isMobile }) =>
    isMobile &&
    css`
      height: ${otherVariables.trackMobileHeight};
    `};

  ${({ type }) =>
    type === 'volume' &&
    css`
      top: calc(50% - 3px);
    `}

  ${({ activeThumb }) =>
    activeThumb &&
    css`
      opacity: 1;
      transform: scale(1);
    `}

  ${({ isMobile, type }) =>
    isMobile &&
    type === 'volume' &&
    css`
      height: 5px;
      top: calc(50% - 2.5px);
    `}
`;

export const TrackThumb = styled.div`
  position: absolute;
  width: 25px;
  height: 15px;
  right: 0;
  top: 50%;
  transform: translate(50%, -50%);
  z-index: 1;

  &:after {
    content: '';
    display: block;
    position: absolute;
    width: 7px;
    height: 12px;
    background-color: ${({ theme }) => colorVariables[theme.main]};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }
`;

export const Content = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const ContentWrap = styled.div`
  position: relative;
  width: 100%;

  &:focus {
    outline: none;
  }

  ${(props) =>
    props.type === 'volume' &&
    css`
      visibility: hidden;
      opacity: 0;
      width: 0;
      padding: 0 0 5px;
      height: 40px;
      overflow: hidden;
      transition: width 200ms cubic-bezier(1, 0, 1, 0), opacity 175ms ease-in,
        visibility 175ms ease-in;
    `};

  ${({ type, isMobile }) =>
    type === 'volume' &&
    !isMobile &&
    css`
      ${SliderStyled}:hover & {
        visibility: visible;
        opacity: 1;
        width: 80px;
        transition: 135ms ease-out;
      }
    `};

  ${({ seeking }) =>
    seeking &&
    css`
      visibility: visible;
      opacity: 1;
      width: 80px;
      transition: 135ms ease-out;
    `};
`;

export const Track = styled.div`
  width: 100%;
  height: 100%;
`;

export const TrackContent = styled.div`
  position: relative;
  height: 16px;
  cursor: pointer;
  overflow: hidden;
  outline: none;

  &:focus {
    outline: none;
  }

  ${({ type }) =>
    type === 'volume' &&
    css`
      padding: 0;
      height: 100%;
      width: 80px;
    `};
`;

export const Buffered = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.3);
  transform: scaleX(0.8);
  transform-origin: 0 0;
  transition: transform 5s;
`;

export const AdTimeLabel = styled.div`
  position: absolute;
  width: 3px;
  height: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.3);

  ${({ point }) =>
    css`
      left: ${point}%;
    `};
`;

export const SeekHover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  background-color: rgba(255, 255, 255, 0.2);
  z-index: 2;
  transform: scaleX(0.6);
  transform-origin: 0 0;

  ${ContentWrap}:hover & {
    opacity: 1;
  }
`;

export const Connect = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => _.get(colorVariables, theme.main, colorVariables.blue_doger)};
  transform-origin: 0 0;
  transition: transform 0.1s;
  ${({ isMobile, theme }) =>
    isMobile &&
    css`
      background-color: ${_.get(colorVariables, theme.main_default, colorVariables.blue_doger)};
    `};
`;

export const Thumb = styled.div`
  pointer-events: none;
  position: absolute;
  width: 12px;
  height: 12px;
  left: -6px;
  top: 4px;
  z-index: 4;
  transform: translateX(100px);
`;

export const Handler = styled.div`
  transition: transform 0.1s;
  border-radius: 100%;
  width: 100%;
  height: 100%;
  background-color: red;
  opacity: 0;
  transform: scale(0.4);

  ${({ activeThumb }) =>
    activeThumb &&
    css`
      opacity: 1;
      transform: scale(1);
    `};
`;

export const HoverThumbnail = styled.div`
  position: absolute;
  opacity: 0;
  transform: translateX(150px);
  pointer-events: none;
  text-align: center;
  z-index: 10;

  ${({ activeThumb }) =>
    activeThumb &&
    css`
      opacity: 1;
      transform: scale(1);
    `};
`;

export const TimeThumbnail = styled.div`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  font-family: ${fontFamilyVariables.din_regular};
  color: white;
  font-size: ${fontVariables.fs_xxs};
`;

export const Thumbnail = styled.div`
  border: none;
  outline: none;

  ${({ thumbnails, multiplier }) =>
    css`
      display: block;
      width: ${thumbnails.src ? `${thumbnails.width * multiplier}` : 96}px;
      height: ${thumbnails.height * multiplier}px;
      background-size: auto ${thumbnails.height * multiplier}px;
      background-image: url(${thumbnails.src});
      background-position: ${thumbnails.offset * multiplier}px;
    `};

  ${({ thumbnails }) =>
    thumbnails.src
      ? css`
          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 30px;
            background-image: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
            z-index: 2;
          }
        `
      : css`
          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 30px;
            background-color: black;
            z-index: -1;
          }
        `};
`;

export const VolumeButton = styled.div`
  position: relative;
`;

export const StyledButton = styled(Button)`
  && {
    padding: 6px 12px 13px;

    ${({ isMobile }) =>
      isMobile &&
      css`
        padding: 9px 10px 17px;
      `};
  }
`;
